import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { TimePicker } from '@/components/ui/time-picker';
import useFormattedDate from '@/hooks/useFormattedDate';
import { cn } from '@/lib/utils';
import serviceGroupSchema from '@/schemas/serviceGroup';
import { zodResolver } from '@hookform/resolvers/zod';
import { CalendarIcon, Loader } from 'lucide-react';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ServiceGroupFormProps, TForm } from './ServiceGroupForm.types';

const ServiceGroupForm: React.FC<ServiceGroupFormProps> = ({
  defaultValues,
  onSubmit,
  isPending,
  onDelete,
  isPendingDelete
}) => {
  const { t } = useTranslation();
  const { formattedDate } = useFormattedDate();
  const form = useForm<TForm>({
    resolver: zodResolver(serviceGroupSchema),
    defaultValues: defaultValues,
    mode: 'onChange'
  });

  const renderButtonContent = (isEdit: boolean, isPending: boolean) => {
    if (isPending) {
      return <Loader className="w-4 h-4 animate-spin" />;
    }
    if (isEdit) {
      return t('form.update');
    }
    return t('form.create');
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-8 rounded-md"
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('form.name')}</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t('form.description')}
                <span className="ml-2 text-sm font-light text-gray-500">
                  {t('form.optional')}
                </span>
              </FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="startTime"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <FormLabel>{t('form.startTimeGroup.label')}</FormLabel>
              <FormDescription>
                {t('form.startTimeGroup.description')}
              </FormDescription>
              <FormControl>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant={'outline'}
                      className={cn(
                        'w-full justify-start text-left font-normal',
                        !field.value && 'text-muted-foreground'
                      )}
                    >
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {field.value ? (
                        formattedDate({
                          date: field.value,
                          dateFormat: 'dateShort'
                        })
                      ) : (
                        <span>{t('form.startTimeGroup.placeholder')}</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={field.onChange}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="startHour"
          render={({ field }) => (
            <FormItem className="flex flex-col w-full">
              <FormLabel>{t('form.startHour.label')}</FormLabel>
              <FormDescription>
                {t('form.startHour.placeholder')}
              </FormDescription>
              <FormControl>
                <TimePicker
                  date={field.value}
                  setDate={(date) => field.onChange(date)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="endTime"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <FormLabel>{t('form.endTimeGroup.label')}</FormLabel>
              <FormDescription>
                {t('form.endTimeGroup.description')}
              </FormDescription>
              <FormControl>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant={'outline'}
                      className={cn(
                        'w-full justify-start text-left font-normal',
                        !field.value && 'text-muted-foreground'
                      )}
                    >
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {field.value ? (
                        formattedDate({
                          date: field.value,
                          dateFormat: 'dateShort'
                        })
                      ) : (
                        <span>{t('form.endTimeGroup.placeholder')}</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={field.onChange}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="duration"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('form.duration')}</FormLabel>
              <FormDescription>{t('form.durationPlaceholder')}</FormDescription>
              <FormControl>
                <Input type="number" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex justify-end gap-2">
          {onDelete && (
            <Button
              type="button"
              disabled={isPendingDelete}
              variant={'destructive'}
              onClick={onDelete}
            >
              {isPendingDelete ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                t('form.delete')
              )}
            </Button>
          )}
          <Button disabled={isPending}>
            {renderButtonContent(!!defaultValues, isPending)}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default ServiceGroupForm;
